import React, { useEffect, useRef } from 'react';

type RenameLocation = {
  filePath: string;
  line: number;
  column: number;
  length: number;
};

type RenameModalProps = {
  isOpen: boolean;
  symbolName: string;
  newName: string;
  locations: RenameLocation[];
  isRenaming: boolean;
  onNewNameChange: (name: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
};

export const RenameModal = React.memo(function RenameModal({
  isOpen,
  symbolName,
  newName,
  locations,
  isRenaming,
  onNewNameChange,
  onConfirm,
  onCancel,
}: RenameModalProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
      } else if (e.key === 'Enter') {
        e.preventDefault();
        onConfirm();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onCancel, onConfirm]);

  if (!isOpen) return null;

  // Group locations by file
  const groupedLocations = locations.reduce((acc, loc) => {
    if (!acc[loc.filePath]) {
      acc[loc.filePath] = [];
    }
    acc[loc.filePath].push(loc);
    return acc;
  }, {} as Record<string, RenameLocation[]>);

  const fileCount = Object.keys(groupedLocations).length;

  return (
    <>
      <div className="rename-modal-overlay" onClick={onCancel} />
      <div className="rename-modal">
        <div className="rename-modal-header">
          <h3 className="rename-modal-title">Rename Symbol</h3>
          <button className="rename-modal-close" onClick={onCancel}>×</button>
        </div>

        <div className="rename-modal-content">
          <div className="rename-input-group">
            <label className="rename-label">
              Rename <strong>{symbolName}</strong> to:
            </label>
            <input
              ref={inputRef}
              type="text"
              className="rename-input"
              value={newName}
              onChange={(e) => onNewNameChange(e.target.value)}
              placeholder="New name..."
            />
          </div>

          <div className="rename-preview">
            <div className="rename-preview-header">
              <span className="rename-preview-title">Preview</span>
              <span className="rename-preview-count">
                {locations.length} occurrence{locations.length !== 1 ? 's' : ''} in {fileCount} file{fileCount !== 1 ? 's' : ''}
              </span>
            </div>
            <div className="rename-preview-list">
              {Object.entries(groupedLocations).slice(0, 5).map(([filePath, locs]) => (
                <div key={filePath} className="rename-preview-file">
                  <span className="rename-preview-file-name">
                    {filePath.split('/').pop()}
                  </span>
                  <span className="rename-preview-file-path">
                    {filePath}
                  </span>
                  <span className="rename-preview-file-count">
                    {locs.length}
                  </span>
                </div>
              ))}
              {fileCount > 5 && (
                <div className="rename-preview-more">
                  ... and {fileCount - 5} more file{fileCount - 5 !== 1 ? 's' : ''}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="rename-modal-footer">
          <button
            className="rename-btn cancel"
            onClick={onCancel}
            disabled={isRenaming}
          >
            Cancel
          </button>
          <button
            className="rename-btn confirm"
            onClick={onConfirm}
            disabled={isRenaming || !newName.trim() || newName === symbolName}
          >
            {isRenaming ? 'Renaming...' : 'Rename'}
          </button>
        </div>
      </div>
    </>
  );
});
